import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { HardDriveUpload, Check, AlertTriangle } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useUsersData } from '../../Helpers/UsersContext';
import { useBuildingImport } from '../../hooks/superadmin/useBuildingImport';
import { formatFileSize } from '../../Helpers/FileUtils';
import SuperadminBreadcrumb from './SuperadminBreadcrumb';

/**
 * Superadmin page: import a building export ZIP (from BuildingExportPage on
 * another instance) into this instance. Shows a preview with warnings before
 * anything is written; overwrite of an existing building requires an explicit
 * confirmation.
 */
const BuildingImportPage: React.FC = () => {
    useDocumentTitle('Importera byggnad');
    const navigate = useNavigate();
    const { users } = useUsersData();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Belt-and-braces guard in addition to the route guard
    const isSuperadmin = useMemo(() => {
        const uid = getAuth().currentUser?.uid;
        if (!uid || !users) return null; // null = still loading
        return users.find(u => u.key === uid)?.superadmin || false;
    }, [users]);

    useEffect(() => {
        if (isSuperadmin === false) {
            navigate('/superadmin');
        }
    }, [isSuperadmin, navigate]);

    const {
        phase,
        preview,
        remapAllToMe,
        setRemapAllToMe,
        confirmOverwrite,
        setConfirmOverwrite,
        progress,
        result,
        error,
        selectFile,
        runImport,
        reset,
    } = useBuildingImport();

    const missingConfigCount = preview
        ? preview.missingConfig.componentTypeKeys.length +
          preview.missingConfig.materialKeys.length +
          preview.missingConfig.tagKeys.length
        : 0;
    const unknownUsers = preview ? preview.referencedUsers.filter(user => !user.existsInTarget) : [];
    const canImport = phase === 'preview' && (!preview?.buildingExists || confirmOverwrite);

    return (
        <>
        <SuperadminBreadcrumb title="Importera byggnad" />
        <Container className="py-4" style={{ maxWidth: 700 }}>
            <h1 className="page-title mb-4">Importera byggnad</h1>

            <Card>
                <Card.Header className="d-flex align-items-center gap-2">
                    <HardDriveUpload size={18} />
                    Byggnadsimport
                </Card.Header>
                <Card.Body className="p-4">
                    <p className="text-muted mb-3">
                        Importerar en byggnadsexport (ZIP-fil från sidan Exportera byggnad i en
                        annan instans) till den här instansen. Ingenting skrivs förrän du
                        bekräftar förhandsgranskningen.
                    </p>

                    {error && <Alert variant="danger">{error}</Alert>}

                    {(phase === 'idle' || phase === 'loadingPreview') && (
                        <>
                            <Form.Group controlId="import-building-file" className="mb-3">
                                <Form.Label>Exportfil</Form.Label>
                                <Form.Control
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".zip,application/zip,application/x-zip-compressed,application/x-zip"
                                    disabled={phase === 'loadingPreview'}
                                    onChange={event => {
                                        const file = (event.target as HTMLInputElement).files?.[0];
                                        if (file) selectFile(file);
                                    }}
                                />
                            </Form.Group>
                            {phase === 'loadingPreview' && (
                                <div className="d-flex align-items-center gap-2 text-muted">
                                    <Spinner size="sm" />
                                    Läser filen och jämför med instansen...
                                </div>
                            )}
                        </>
                    )}

                    {preview && phase !== 'idle' && phase !== 'loadingPreview' && (
                        <>
                            <div className="mb-3">
                                <strong>{preview.buildingName}</strong>{' '}
                                <span className="text-muted">({preview.buildingKey})</span>
                                <div className="text-muted">
                                    {preview.counts.components} komponenter, {preview.counts.binaries} filer (
                                    {formatFileSize(preview.counts.totalBytes)}),{' '}
                                    {preview.referencedUsers.length} refererade användare
                                </div>
                            </div>

                            {preview.appVersionWarning && (
                                <Alert variant="warning">{preview.appVersionWarning}</Alert>
                            )}

                            {missingConfigCount > 0 && (
                                <Alert variant="warning">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <AlertTriangle size={16} />
                                        <strong>{missingConfigCount} konfigurationsposter saknas i denna instans.</strong>
                                    </div>
                                    Kör konfigurationsimporten (Konfigurationsstatus) från samma
                                    källinstans först, annars visas berörda komponenter utan typ,
                                    material eller tagg tills konfigurationen synkats.
                                    <ul className="mb-0 mt-2 ps-3">
                                        {preview.missingConfig.componentTypeKeys.length > 0 && (
                                            <li>Komponenttyper: {preview.missingConfig.componentTypeKeys.join(', ')}</li>
                                        )}
                                        {preview.missingConfig.materialKeys.length > 0 && (
                                            <li>Material: {preview.missingConfig.materialKeys.join(', ')}</li>
                                        )}
                                        {preview.missingConfig.tagKeys.length > 0 && (
                                            <li>Byggnadstaggar: {preview.missingConfig.tagKeys.join(', ')}</li>
                                        )}
                                    </ul>
                                </Alert>
                            )}

                            {preview.skippedStoragePaths.length > 0 && (
                                <Alert variant="warning">
                                    {preview.skippedStoragePaths.length} refererade filer ingick inte i
                                    exporten (låg utanför byggnadens lagringsyta) och kommer sakna innehåll.
                                </Alert>
                            )}

                            {unknownUsers.length > 0 && !remapAllToMe && (
                                <Alert variant="info">
                                    {unknownUsers.length} refererade användare finns inte i den här
                                    instansen och visas som okänd användare:{' '}
                                    {unknownUsers.map(user => user.name || user.uid).join(', ')}
                                </Alert>
                            )}

                            {preview.buildingExists && (
                                <Alert variant="danger">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <AlertTriangle size={16} />
                                        <strong>
                                            Byggnaden finns redan i den här instansen och skrivs över helt.
                                        </strong>
                                    </div>
                                    Befintliga komponenter, foton och dokument för byggnaden ersätts
                                    med innehållet i filen.
                                    <Form.Check
                                        type="checkbox"
                                        id="confirm-overwrite"
                                        className="mt-2"
                                        label={`Ja, skriv över ${preview.buildingName}`}
                                        checked={confirmOverwrite}
                                        disabled={phase !== 'preview'}
                                        onChange={event => setConfirmOverwrite(event.target.checked)}
                                    />
                                </Alert>
                            )}

                            <Form.Check
                                type="checkbox"
                                id="remap-all-to-me"
                                className="mb-4"
                                label="Skriv om alla användarreferenser till mig (annars behålls käll-instansens användar-ID:n)"
                                checked={remapAllToMe}
                                disabled={phase !== 'preview'}
                                onChange={event => setRemapAllToMe(event.target.checked)}
                            />

                            {phase === 'importing' && (
                                <div className="mb-4">
                                    <div className="text-muted mb-1">
                                        {progress?.phase === 'uploading'
                                            ? `Laddar upp filer (${progress.filesDone} av ${progress.filesTotal})...`
                                            : 'Skriver byggnadsdata till databasen...'}
                                    </div>
                                    <ProgressBar
                                        animated={!progress || progress.phase === 'database'}
                                        now={
                                            progress?.phase === 'uploading' && progress.filesTotal > 0
                                                ? (100 * progress.filesDone) / progress.filesTotal
                                                : 100
                                        }
                                    />
                                </div>
                            )}

                            {result && phase === 'done' && (
                                <Alert variant={result.failedFiles.length > 0 ? 'warning' : 'success'}>
                                    <div className="d-flex align-items-center gap-2">
                                        <Check size={18} />
                                        Byggnaden importerades. {result.uploadedFiles} filer uppladdade
                                        {result.existingFiles > 0
                                            ? `, ${result.existingFiles} fanns redan och behölls.`
                                            : '.'}
                                    </div>
                                    {result.failedFiles.length > 0 && (
                                        <>
                                            <div className="mt-2">
                                                {result.failedFiles.length} filer kunde inte laddas upp:
                                            </div>
                                            <ul className="mb-0 ps-3">
                                                {result.failedFiles.map(failure => (
                                                    <li key={failure.storagePath}>
                                                        {failure.storagePath} — {failure.message}
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </Alert>
                            )}

                            <div className="d-flex gap-2">
                                {phase !== 'done' && (
                                    <Button variant="primary" disabled={!canImport} onClick={runImport}>
                                        {phase === 'importing' ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Importerar...
                                            </>
                                        ) : (
                                            <>
                                                <HardDriveUpload size={16} className="me-1" />
                                                Importera byggnad
                                            </>
                                        )}
                                    </Button>
                                )}
                                <Button
                                    variant="outline-secondary"
                                    disabled={phase === 'importing'}
                                    onClick={() => {
                                        reset();
                                        if (fileInputRef.current) fileInputRef.current.value = '';
                                    }}
                                >
                                    {phase === 'done' ? 'Importera en till' : 'Avbryt'}
                                </Button>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
        </>
    );
};

export default BuildingImportPage;
